import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { baseStyles } from '../styles/shared.js';
import { generationChanged, SECONDARY_SIDEBAR_TABS } from '../utils/constants.js';
import type { SecondarySidebarTab } from '../utils/constants.js';
import type { ModelerController } from '../controllers/modeler-controller.js';
import type { DocumentController } from '../controllers/document-controller.js';
import type { ChatbotController } from '../controllers/chatbot-controller.js';
import type { IngestionController } from '../controllers/ingestion-controller.js';

// ---------------------------------------------------------------------------
// <sysml-secondary-sidebar> — Right sidebar with mini icon bar
//
// Replaces the old absolute-positioned right panel overlay.
// Left: panel content area. Right: 36px vertical icon bar for tab switching.
// ---------------------------------------------------------------------------

@customElement('sysml-secondary-sidebar')
export class SysmlSecondarySidebar extends LitElement {
  static override styles = [
    baseStyles,
    css`
      :host {
        display: flex;
        flex-direction: row;
        overflow: hidden;
        border-left: 1px solid var(--r5-border);
        background: var(--r5-surface);
      }

      /* Content area */
      .ss-content {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        display: flex;
        flex-direction: column;
      }

      /* Mini icon bar on the right edge */
      .ss-icon-bar {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 36px;
        flex-shrink: 0;
        border-left: 1px solid var(--r5-border);
        background: var(--r5-surface-muted);
        padding-top: var(--r5-space-1);
        overflow-y: auto;
        overflow-x: hidden;
      }
      .ss-icon-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        border: none;
        border-radius: var(--r5-radius-sm);
        background: none;
        color: var(--r5-text-muted);
        cursor: pointer;
        transition: all var(--r5-duration-fast) var(--r5-ease-default);
        margin-bottom: 2px;
      }
      .ss-icon-btn:hover {
        color: var(--r5-text);
        background: color-mix(in srgb, var(--r5-text) 8%, transparent);
      }
      .ss-icon-btn.active {
        color: var(--r5-primary);
        background: color-mix(in srgb, var(--r5-primary) 12%, transparent);
      }

      /* Drag-and-drop */
      .ss-icon-btn[draggable="true"] { cursor: grab; }
      .ss-icon-btn[draggable="true"]:active { cursor: grabbing; }
      :host(.drop-target) {
        outline: 2px solid var(--r5-primary);
        outline-offset: -2px;
      }

      /* Panel content children fill available space */
      sysml-chatbot-panel, sysml-health-panel, sysml-verification-panel,
      sysml-requirements-panel, sysml-ingest-panel, sysml-builder-wizard,
      sysml-quick-ref {
        flex: 1;
        min-height: 0;
        width: 100%;
      }
    `,
  ];

  @property({ attribute: false, hasChanged: generationChanged }) modeler!: ModelerController;
  @property({ attribute: false, hasChanged: generationChanged }) documents!: DocumentController;
  @property({ attribute: false, hasChanged: generationChanged }) chatbot!: ChatbotController;
  @property({ attribute: false }) ingestion!: IngestionController;
  @property({ attribute: false, type: Number }) ingestionGeneration = 0;
  @property({ attribute: false }) activeTab: SecondarySidebarTab = 'chatbot';
  @property({ attribute: false }) theme: 'light' | 'dark' = 'dark';
  /** When true, the internal icon bar is hidden (workspace renders it externally) */
  @property({ type: Boolean, attribute: 'hide-icon-bar' }) hideIconBar = false;

  override render() {
    return html`
      <div class="ss-content" role="tabpanel" aria-label=${this.activeTab}>
        ${this._renderContent()}
      </div>
      ${this.hideIconBar
        ? nothing
        : html`
            <div
              class="ss-icon-bar"
              role="tablist"
              aria-label="Secondary sidebar tabs"
              aria-orientation="vertical"
              @dragover=${this._onDragOver}
              @dragleave=${this._onDragLeave}
              @drop=${this._onDrop}
            >
              ${SECONDARY_SIDEBAR_TABS.map(
                (tab) => html`
                  <button
                    class="ss-icon-btn ${this.activeTab === tab.id ? 'active' : ''}"
                    role="tab"
                    aria-selected=${this.activeTab === tab.id ? 'true' : 'false'}
                    aria-label=${tab.label}
                    title=${tab.label}
                    draggable="true"
                    @dragstart=${(e: DragEvent) => this._onDragStart(e, tab.id)}
                    @click=${() => this._onTabClick(tab.id)}
                  >
                    <ark-icon name=${tab.icon} size="sm"></ark-icon>
                  </button>
                `,
              )}
            </div>
          `}
    `;
  }

  private _renderContent() {
    switch (this.activeTab) {
      case 'chatbot':
        return html`
          <sysml-chatbot-panel
            .chatbot=${this.chatbot}
            .modeler=${this.modeler}
            @apply-suggestion=${(e: CustomEvent) =>
              this._emitEvent('apply-suggestion', e.detail)}
          ></sysml-chatbot-panel>
        `;
      case 'health':
        return html`
          <sysml-health-panel
            .modeler=${this.modeler}
          ></sysml-health-panel>
        `;
      case 'verification':
        return html`
          <sysml-verification-panel
            .modeler=${this.modeler}
            @insert-code=${(e: CustomEvent) =>
              this._emitEvent('insert-code', e.detail)}
          ></sysml-verification-panel>
        `;
      case 'requirements':
        return html`
          <sysml-requirements-panel
            .modeler=${this.modeler}
            .documents=${this.documents}
            @insert-code=${(e: CustomEvent) =>
              this._emitEvent('insert-code', e.detail)}
          ></sysml-requirements-panel>
        `;
      case 'ingest':
        return html`
          <sysml-ingest-panel
            .ingestion=${this.ingestion}
            .ingestionGeneration=${this.ingestionGeneration}
            @insert-code=${(e: CustomEvent) =>
              this._emitEvent('insert-code', e.detail)}
          ></sysml-ingest-panel>
        `;
      case 'builder':
        return html`
          <sysml-builder-wizard
            .modeler=${this.modeler}
            .documents=${this.documents}
            @insert-code=${(e: CustomEvent) =>
              this._emitEvent('insert-code', e.detail)}
          ></sysml-builder-wizard>
        `;
      case 'quickRef':
        return html`
          <sysml-quick-ref
            .collapsed=${false}
            @quickRefInsert=${(e: CustomEvent) =>
              this._emitEvent('quick-ref-insert', e.detail)}
          ></sysml-quick-ref>
        `;
    }
  }

  private _onTabClick(tab: SecondarySidebarTab): void {
    this.dispatchEvent(
      new CustomEvent('tab-change', {
        detail: { tab },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private _emitEvent(name: string, detail: unknown): void {
    this.dispatchEvent(
      new CustomEvent(name, { detail, bubbles: true, composed: true }),
    );
  }

  // -- Drag-and-drop ----------------------------------------------------------

  private _onDragStart(e: DragEvent, tabId: string): void {
    e.dataTransfer?.setData('text/plain', JSON.stringify({ zone: 'secondary', tabId }));
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
  }

  private _onDragOver(e: DragEvent): void {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    this.classList.add('drop-target');
  }

  private _onDragLeave(): void {
    this.classList.remove('drop-target');
  }

  private _onDrop(e: DragEvent): void {
    e.preventDefault();
    this.classList.remove('drop-target');
    try {
      const data = JSON.parse(e.dataTransfer?.getData('text/plain') ?? '{}');
      if (data.zone && data.tabId) {
        this.dispatchEvent(
          new CustomEvent('panel-drop', {
            detail: { sourceZone: data.zone, tabId: data.tabId, targetZone: 'secondary' },
            bubbles: true,
            composed: true,
          }),
        );
      }
    } catch { /* ignore invalid drag data */ }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sysml-secondary-sidebar': SysmlSecondarySidebar;
  }
}
